import axios, { AxiosResponse } from 'axios';
import {
  Answer,
  AnswerType,
  AssignedQuestionnaire,
  AutocompleteAnswer,
  AutocompleteQuestion,
  AutocompleteQuestionOption,
  Choice,
  ChoiceAnswer,
  ConvertibleUnit,
  FixedChoices,
  HospitalQuestionnaireDto,
  IDK_CHOICE_CODE,
  isAutocompleteAnswer,
  isAutocompleteQuestion,
  isBiChoiceQuestion,
  isChoiceAnswer,
  isChoiceQuestion,
  isUploadAnswer,
  isUploadQuestion,
  NONE_CHOICE_CODE,
  Question,
  QuestionnaireQuestionResponse,
  QuestionnaireStatus,
  QuestionnaireStatusResponse,
  QuestionType,
  toAutocompleteOption,
  toChoice,
  UploadAnswer,
  UploadQuestion
} from './model';
import { SentryService } from '@ianesthesie-kit/sentry';
import { valueOfEnum } from '@ianesthesie-kit/core';
import {
  ID,
  ListApiResponse,
  MediaService,
  Medium,
  MediumType,
  SingleApiResponse
} from '@ianesthesie-kit/services';
import { stringify } from 'query-string';
import { captureThrowOnDev, toKebabQuery } from '@/util';
import uniqBy from 'lodash/fp/uniqBy';

type ChoiceGroup = {
  type: 'PRIMARY' | 'SECONDARY';
  choices: Choice[];
};

export type FetchAssignedQuestionnaireParams = {
  statuses?: QuestionnaireStatus[];
};

export type FetchAutocompleteQuestionOptionsParams = {
  questionId: AutocompleteQuestion['id'];
  assignedQuestionnaireId: AssignedQuestionnaire['id'];
  search?: string;
  limit?: number;
};

export interface RawQuestionResponse {
  id: ID;
  answers?: Record<ID, Answer>;
  questionnaireQuestions: {
    question: Question;
    orderIndex: number;
    groupName?: string;
    groupTitleId?: string;
  }[];
  lastQuestion?: boolean;
  totalQuestions: number;
  questionnaireStatus: string;
  groupName?: string;
  groupTitleId?: string;
  pediatric?: boolean;
  questionnaireCode: string;
}

export type SkipOptions = {
  postpone?: boolean;
  manualEntry?: boolean;
};

export type GroupAnswerDataElement = {
  question: Question;
  value: Answer['value'];
};

export class QuestionnaireService {
  public static readonly USER_DEFINED_CHOICE_CODE = 'USER_DEFINED';
  public static readonly AUTOCOMPLETE_USER_INPUT_CODE = 'FREESOLO_VALUE';

  // must align with backend
  private static readonly AUTOCOMPLETE_CODE_DELIM = '###';

  private static readonly INSTANCE: QuestionnaireService =
    new QuestionnaireService();

  private readonly url;

  private fixedChoices?: {
    idk: Choice;
    none: Choice;
  };

  private constructor() {
    this.url = '/bff/patient/questionnaire';
  }

  public static getInstance(): QuestionnaireService {
    return this.INSTANCE;
  }

  public fetchAssignedQuestionnaires(
    filters?: FetchAssignedQuestionnaireParams
  ): Promise<ListApiResponse<AssignedQuestionnaire>> {
    let query = '';
    if (filters?.statuses) {
      query = stringify({ status: filters.statuses });
    }
    return axios
      .get<ListApiResponse<AssignedQuestionnaire>>(
        `${this.url}/assigned?${query}`
      )
      .then((resp) => resp.data);
  }

  public fetchHospitalQuestionnaires(): Promise<
    ListApiResponse<HospitalQuestionnaireDto>
  > {
    return axios
      .get<ListApiResponse<HospitalQuestionnaireDto>>(this.url)
      .then((resp) => resp.data);
  }

  public async fetchAutocompleteQuestionOptions({
    questionId,
    ...params
  }: FetchAutocompleteQuestionOptionsParams): Promise<
    AutocompleteQuestionOption[]
  > {
    const qs = toKebabQuery(params);
    const r = await axios.get<ListApiResponse<AutocompleteQuestionOption>>(
      `${this.url}/question/autocomplete/${questionId}/options${qs}`
    );
    return r.data.data;
  }

  public searchMultipleAutocompleteQuestionOptions({
    search,
    ...params
  }: Omit<FetchAutocompleteQuestionOptionsParams, 'search'> & {
    search: string[];
  }): Promise<AutocompleteQuestionOption[]> {
    if (!search?.filter(Boolean).length) {
      return Promise.resolve([]);
    }
    return Promise.all(
      search
        .map((s) => s.split(QuestionnaireService.AUTOCOMPLETE_CODE_DELIM))
        .flat()
        .map((s) =>
          this.fetchAutocompleteQuestionOptions({ search: s, ...params })
        )
    ).then((resp) => uniqBy('id', resp.flat()));
  }

  public currentStatus(aqId: ID): Promise<QuestionnaireStatusResponse> {
    return axios
      .get<SingleApiResponse<RawQuestionResponse>>(
        `${this.url}/assigned/${aqId}/status`
      )
      .then((r) => ({
        questionnaireStatus: valueOfEnum(
          r.data.data.questionnaireStatus,
          QuestionnaireStatus,
          {
            throwIfNotFound: true,
            caseSensitive: false
          }
        )!
      }));
  }

  public currentQuestion(
    aqId: ID,
    skipSentryReporting?: boolean
  ): Promise<QuestionnaireQuestionResponse> {
    return axios
      .get<SingleApiResponse<RawQuestionResponse>>(
        `${this.url}/assigned/${aqId}/current`
      )
      .then((r) => this.adaptResponse(r))
      .catch((err) => {
        if (!skipSentryReporting) {
          SentryService.getInstance()
            .addBreadcrumb({
              level: 'error',
              message: 'Initial question failed to load',
              data: err
            })
            .captureException(err);
        }

        throw err;
      });
  }

  public previousQuestion(aqId: ID): Promise<QuestionnaireQuestionResponse> {
    return axios
      .post<SingleApiResponse<RawQuestionResponse>>(
        `${this.url}/assigned/${aqId}/previous`
      )
      .then((r) => this.adaptResponse(r))
      .catch((err) => {
        SentryService.getInstance()
          .addBreadcrumb({
            level: 'error',
            message: 'Previous question failed to load',
            data: err
          })
          .captureException(err);

        throw err;
      });
  }

  /* eslint-disable prettier/prettier */
  public answerQuestion(
    aqId: ID,
    question: Question,
    value: Answer['value']
  ): Promise<QuestionnaireQuestionResponse>;
  public answerQuestion(
    aqId: ID,
    answerData: GroupAnswerDataElement[]
  ): Promise<QuestionnaireQuestionResponse>;
  /* eslint-enable prettier/prettier */
  public answerQuestion(
    aqId: ID,
    questionOrGroupAnswers: Question | GroupAnswerDataElement[],
    valueIfNotGroup?: Answer['value']
  ): Promise<QuestionnaireQuestionResponse> {
    const isGroup = Array.isArray(questionOrGroupAnswers);
    const dataList = isGroup
      ? questionOrGroupAnswers
      : [{ question: questionOrGroupAnswers, value: valueIfNotGroup }];
    const answers: Answer[] = [];

    dataList.forEach(({ question, value }) => {
      const answerType = this.mapToAnswerType(question.type);
      const answer = {
        questionId: question.id,
        value,
        type: answerType,
        manuallySkipped: false
      } as Answer;

      if (isUploadAnswer(answer) && value) {
        throw new Error('Upload answers need special handling');
      }

      if (isChoiceAnswer(answer) && value) {
        const choices: Choice[] = [];
        const userInput: string[] = [];
        (value as ChoiceAnswer['value'])?.forEach((c) => {
          if (c.code == QuestionnaireService.USER_DEFINED_CHOICE_CODE) {
            userInput.push(c.textId);
          } else {
            choices.push(c);
          }
        });

        answer.value = choices;
        if (userInput.length) {
          (answer as any).userInput = userInput;
        }
      } else if (isAutocompleteAnswer(answer) && value) {
        const choices: AutocompleteQuestionOption[] = [];
        const userInput: string[] = [];
        (value as AutocompleteAnswer['value'])?.forEach((c) => {
          if (c.code === QuestionnaireService.AUTOCOMPLETE_USER_INPUT_CODE) {
            userInput.push(c.name);
          } else {
            choices.push(c);
          }
        });

        answer.value = choices;
        if (userInput.length) {
          (answer as any).userInput = userInput;
        }
      }

      answers.push(answer);
    });

    if (isGroup) {
      return this.postAnswer(aqId, answers, dataList[0].question.groupName!);
    }

    return this.postAnswer(aqId, answers[0]);
  }

  public answerUploadQuestion(
    aqId: ID,
    question: UploadQuestion,
    media: (Medium | File)[] = [],
    onUploadProgress?: (e: ProgressEvent) => void
  ): Promise<QuestionnaireQuestionResponse> {
    if (!isUploadQuestion(question)) {
      throw new Error('Non Upload question passed');
    }

    const existingMediaIds = (
      media.filter((m) => !(m instanceof File)) as Medium[]
    ).map((m) => m.id);

    const files = media.filter((m) => m instanceof File) as File[];

    try {
      MediaService.assertFileSizeOrThrow(files);
    } catch (err) {
      return Promise.reject(err);
    }

    const answer = {
      questionId: question.id,
      value: existingMediaIds,
      type: AnswerType.UPLOAD,
      manuallySkipped: false
    } as Answer;

    const formData = new FormData();
    formData.append('answer', JSON.stringify(answer));
    files.forEach((f) => {
      formData.append('files', f);
    });

    return axios
      .post<SingleApiResponse<RawQuestionResponse>>(
        `${this.url}/assigned/${aqId}/next/upload`,
        formData,
        {
          onUploadProgress
        }
      )
      .then((r) => this.adaptResponse(r))
      .catch(async (err) => {
        let serializedFiles: Array<FileReader['result']> = [];

        try {
          serializedFiles = await Promise.all(
            files.map(async (file) => await this.serializeFile(file))
          );
        } catch {
          /* noop */
        }

        SentryService.getInstance()
          .addBreadcrumb({
            level: 'error',
            message: 'Question could not be answered',
            data: {
              error: err,
              answer: answer,
              serializedFiles
            }
          })
          .captureException(err);

        throw err;
      });
  }

  public skipQuestion(
    aqId: ID,
    question: Question,
    skipOptions?: SkipOptions
  ): Promise<QuestionnaireQuestionResponse> {
    if (isUploadQuestion(question)) {
      return this.skipUploadQuestion(aqId, question, skipOptions);
    }
    const skipAnswer = this.createSkipAnswer(question);
    return this.postAnswer(aqId, skipAnswer);
  }

  public skipUploadQuestion(
    aqId: ID,
    question: UploadQuestion,
    skipOptions?: SkipOptions
  ): Promise<QuestionnaireQuestionResponse> {
    if (!isUploadQuestion(question)) {
      throw new Error('Non Upload question passed');
    }

    const { postpone, manualEntry } = skipOptions ?? {};

    const answer = this.createSkipAnswer(question) as UploadAnswer;
    answer.postponed = postpone;
    if (manualEntry === true) {
      answer.manualEntrySelected = true;
    }
    if (manualEntry === false) {
      answer.manualEntrySkipped = true;
    }

    if (postpone && manualEntry) {
      captureThrowOnDev(
        new Error('Cannot postpone and manually enter at the same time')
      );
      answer.postponed = false;
    }

    const formData = new FormData();
    formData.append('answer', JSON.stringify(answer));

    return axios
      .post<SingleApiResponse<RawQuestionResponse>>(
        `${this.url}/assigned/${aqId}/next/upload`,
        formData
      )
      .then((r) => this.adaptResponse(r))
      .catch((err) => {
        SentryService.getInstance()
          .addBreadcrumb({
            level: 'error',
            message: 'Question could not be skipped',
            data: {
              error: err,
              answer: answer
            }
          })
          .captureException(err);

        throw err;
      });
  }

  public fetchFixedChoices(forceRefetch?: boolean): Promise<FixedChoices> {
    if (this.fixedChoices && !forceRefetch) {
      return Promise.resolve(this.fixedChoices);
    }
    return axios
      .get<ListApiResponse<Choice>>(`${this.url}/choice/fixed`)
      .then((response) => response.data.data)
      .then((data) => {
        const idk = data.find((c) => c.code === IDK_CHOICE_CODE);
        const none = data.find((c) => c.code === NONE_CHOICE_CODE);

        if (!idk || !none) {
          return Promise.reject(new Error('Error fetching fixed choices'));
        }

        this.fixedChoices = {
          idk,
          none
        };

        return this.fixedChoices;
      });
  }

  /* eslint-disable prettier/prettier */
  private postAnswer(
    aqId: ID,
    answer: Answer
  ): Promise<QuestionnaireQuestionResponse>;
  private postAnswer(
    aqId: ID,
    answer: Answer[],
    groupName: string
  ): Promise<QuestionnaireQuestionResponse>;
  /* eslint-enable prettier/prettier */
  private postAnswer(
    aqId: ID,
    answer: Answer | Answer[],
    groupName?: string
  ): Promise<QuestionnaireQuestionResponse> {
    let url = `${this.url}/assigned/${aqId}/next`;
    let data = Array.isArray(answer)
      ? answer.map((a) => this.adaptOutgoingAnswer(a))
      : this.adaptOutgoingAnswer(answer);
    if (typeof groupName === 'string') {
      url += `?group=${groupName}`;
      data = { answers: data };
    }
    return axios
      .post<SingleApiResponse<RawQuestionResponse>>(url, data)
      .then((r) => this.adaptResponse(r))
      .catch((err) => {
        SentryService.getInstance()
          .addBreadcrumb({
            level: 'error',
            message: 'Question could not be answered',
            data: {
              error: err,
              answer: answer
            }
          })
          .captureException(err);

        throw err;
      });
  }

  private createSkipAnswer(question: Question): Answer {
    const answerType = this.mapToAnswerType(question.type);
    return {
      type: answerType,
      value: null,
      questionId: question.id,
      manuallySkipped: true
    };
  }

  private mapToAnswerType(qt: QuestionType): AnswerType {
    switch (qt) {
      case QuestionType.TEXT:
        return AnswerType.TEXT;
      case QuestionType.NUMBER:
        return AnswerType.NUMBER;
      case QuestionType.DATE:
        return AnswerType.DATE;
      case QuestionType.BI_CHOICE:
      case QuestionType.CHOICE:
        return AnswerType.CHOICE;
      case QuestionType.AUTOCOMPLETE:
        return AnswerType._AUTOCOMPLETE;
      case QuestionType.UPLOAD:
        return AnswerType.UPLOAD;
      case QuestionType.RATING:
        return AnswerType.RATING;
      case QuestionType.STATEMENT:
        return AnswerType.STATEMENT;
      default:
        SentryService.getInstance()
          .addBreadcrumb({
            level: 'error',
            message: `Could not map question type ${qt} to an answer type`
          })
          .captureException(new Error(`Unknown question type: ${qt}`));
        throw new Error(`Unknown question type: ${qt}`);
    }
  }

  private adaptResponse(
    response: AxiosResponse<SingleApiResponse<RawQuestionResponse>>
  ): QuestionnaireQuestionResponse {
    const data = response.data.data;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const questionnaireStatus = valueOfEnum(
      data.questionnaireStatus,
      QuestionnaireStatus,
      {
        throwIfNotFound: true,
        caseSensitive: false
      }
    )!;
    if (questionnaireStatus === QuestionnaireStatus.COMPLETED) {
      return {
        id: data.id,
        totalQuestions: data.totalQuestions,
        questionnaireStatus,
        orderIndex: -1,
        questionnaireCode: data.questionnaireCode,
        pediatric: data.pediatric
      };
    }
    const questions = data.questionnaireQuestions?.map((qq) => {
      const q = this.adaptQuestion(qq.question);
      q.last = !!data.lastQuestion;
      q.groupName = qq.groupName;
      // q.groupTitleId = qq.groupTitleId;
      q.orderIndex = qq.orderIndex;
      return q;
    });

    const answers =
      data.answers &&
      Object.entries(data.answers).reduce((acc, [qid, ans]) => {
        acc[qid] = this.adaptAnswer(ans, questions.find((q) => q.id === qid)!);
        return acc;
      }, {} as Record<ID, Answer>);

    const minOrderIndex = Math.min(
      ...data.questionnaireQuestions.map((qq) => qq.orderIndex)
    );

    return {
      id: data.id,
      questionnaireCode: data.questionnaireCode,
      pediatric: data.pediatric,
      questions,
      answers,
      orderIndex: minOrderIndex,
      totalQuestions: data.totalQuestions,
      questionnaireStatus,
      groupName: data.groupName,
      groupTitleId: data.groupTitleId
    };
  }

  private adaptAnswer(a: any, q: Question): Answer {
    if (!a) {
      return a;
    }
    const adjustedType =
      a.type === AnswerType.CHOICE && isAutocompleteQuestion(q)
        ? AnswerType._AUTOCOMPLETE
        : a.type;
    const adapted = {
      ...a,
      type: valueOfEnum(adjustedType, AnswerType, {
        throwIfNotFound: true
      })
    } as Answer;

    if (isChoiceAnswer(adapted)) {
      if (!isChoiceQuestion(q) && !isBiChoiceQuestion(q)) {
        throw new Error('Answer/question type mismatch');
      }
      const findChoice = (code: string) => {
        if (code === this.fixedChoices?.none.code) {
          return this.fixedChoices.none;
        }
        if (code === this.fixedChoices?.idk.code) {
          return this.fixedChoices.idk;
        }
        let choice: Choice | undefined;
        if (isBiChoiceQuestion(q)) {
          if (q.rightChoice.code === code) {
            choice = q.rightChoice;
          } else if (q.leftChoice.code === code) {
            choice = q.leftChoice;
          }
        } else {
          choice = q.primaryChoices.find((ch) => ch.code === code);
          if (!choice && q.secondaryChoices) {
            choice = q.secondaryChoices.find((ch) => ch.code === code);
          }
        }

        if (!choice) {
          throw new Error(`Choice code ${code} not found`);
        }
        return choice;
      };
      const userDefinedChoices = Array.isArray(a.userInput)
        ? a.userInput.map(toChoice)
        : [];
      const choices = Array.isArray(a.value) ? a.value.map(findChoice) : [];
      adapted.value = [...userDefinedChoices, ...choices];
    } else if (isAutocompleteAnswer(adapted)) {
      // FIXME dirty alert
      if (q.code === 'InsurancePlan') {
        if (a.userInput?.length) {
          adapted.value = [
            {
              id: '-1',
              name: a.userInput[0],
              code: QuestionnaireService.AUTOCOMPLETE_USER_INPUT_CODE
            }
          ];
        }
        if (a.value?.length) {
          const parts = a.value[0].split(
            QuestionnaireService.AUTOCOMPLETE_CODE_DELIM
          );
          if (parts.length === 3) {
            adapted.value = [
              {
                id: '-1',
                group: parts[0],
                name: `${parts[1]} ${parts[2]}`,
                code: a.value[0]
              }
            ];
          } else {
            adapted.value = [
              {
                id: '-1',
                name: a.value[0],
                code: QuestionnaireService.AUTOCOMPLETE_USER_INPUT_CODE
              }
            ];
          }
        }
      } else {
        const selectedOptions = Array.isArray(a.value)
          ? a.value.map((code: string) => ({
              id: code,
              code,
              name: code
            }))
          : [];
        const userDefinedOptions = Array.isArray(a.userInput)
          ? a.userInput.map(toAutocompleteOption)
          : [];
        adapted.value = [...selectedOptions, ...userDefinedOptions];
      }
    }

    return adapted;
  }

  private adaptQuestion(q: any): Question {
    return {
      ...q,
      type: valueOfEnum(q.type, QuestionType, {
        throwIfNotFound: true
      }),
      ...this.adaptFieldsPerType(q)
    } as Question;
  }

  private adaptFieldsPerType(q: any): Partial<Question> | undefined {
    switch (q.type as QuestionType) {
      case QuestionType.CHOICE:
        return {
          ...this.adaptChoices(q.resolvedChoiceGroups)
        };
      case QuestionType.NUMBER:
        return {
          convertibleUnits:
            q.supportedConvertibleUnits &&
            q.supportedConvertibleUnits.map((e: string) =>
              valueOfEnum(e, ConvertibleUnit, {
                throwIfNotFound: true
              })
            )
        };
      case QuestionType.UPLOAD:
        return {
          acceptedTypes: q.acceptedTypes?.map((t: string) =>
            valueOfEnum(t, MediumType, {
              throwIfNotFound: true
            })
          )
        };
    }
  }

  private adaptChoices(resolvedChoiceGroups: any[]) {
    const primaryChoices = resolvedChoiceGroups.find(
      (g: ChoiceGroup) => g.type === 'PRIMARY'
    )?.choices;
    const secondaryChoices = resolvedChoiceGroups
      .find((g: ChoiceGroup) => g.type === 'SECONDARY')
      ?.choices?.filter(
        (ch: Choice) => !primaryChoices.find((c: Choice) => c.id === ch.id)
      );

    return {
      primaryChoices,
      secondaryChoices
    };
  }

  private adaptOutgoingAnswer(answer: Answer): Record<string, unknown> {
    if (isChoiceAnswer(answer) && answer.value) {
      return {
        ...answer,
        value: answer.value.map((c) => c.code)
      };
    }

    if (isAutocompleteAnswer(answer)) {
      return {
        ...answer,
        type: AnswerType.CHOICE,
        value: answer.value?.map((ch) =>
          ch.code === QuestionnaireService.AUTOCOMPLETE_USER_INPUT_CODE
            ? ch.name
            : ch.code
        )
      };
    }

    return { ...answer };
  }

  private serializeFile(file: File): Promise<FileReader['result']> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }
}
